import {
  IonButton,
  IonIcon,
  IonInput,
  useIonLoading,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import AuthHelper from 'lib/auth.common';
import { Auth } from 'aws-amplify';
import { eyeOutline, eyeOffOutline, mailOutline, lockClosedOutline, warningOutline } from 'ionicons/icons';
import * as yup from 'yup';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { onPromise } from '../../CreateAccount';
import { ReduxCommandCreator } from 'appRedux/actions';
import { useDispatch } from 'react-redux';
import { AuthService, IUserInfo } from 'services/auth';
import { AUTH_STATE } from 'appRedux/types/reduxTypes';
import { passwordRegex } from 'common/include';
import { beginApplicationConfigLoginSession } from 'lib/applicationConfig';
import { markResumeLastViewedCasino } from 'lib/lastViewedCasino';
import { clearSession, startSession } from 'lib/sessionExpiry';

interface FormData {
  email: string;
  password: string;
}

export const login_schema = yup.object().shape({
  email: yup.string().email('Email address is not valid').required('Email address is required'),
  password: yup
    .string()
    .matches(
      passwordRegex,
      'Password must include a number, an uppercase letter, a lowercase letter, and a special character, and be at least 8 characters long'
    )
    .required('Password is required'),
});

interface IAWSAuthUser {
  signInUserSession: {
    accessToken: {
      payload: {
        'cognito:groups': Array<string>;
      };
    };
  };
}

export const CasinoSignInAccount = () => {
  const dispatch = useDispatch();
  const commands = ReduxCommandCreator(dispatch);
  const [showPasswordNew, setShowPasswordNew] = useState(false);

  const [presentToast] = useIonToast();
  const [present, dismiss] = useIonLoading();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(login_schema),
  });

  const router = useIonRouter();

  const onSubmit = async (data: FormData) => {
    await present({
      message: 'Loading...',
      cssClass: 'custom-loading',
    });

    try {
      const username = data.email.trim().toLowerCase();
      const password = data.password;
      const user = (await Auth.signIn(username, password)) as IAWSAuthUser;
      // A new login replaces any session record a previous user left behind, so the
      // inactivity check never logs this sign-in out before Login returns.
      clearSession();
      const service = new AuthService();
      service.Login().subscribe({
        next(loginResponse) {
          beginApplicationConfigLoginSession();
          commands.SetAuthState(AUTH_STATE.AUTHENTICATED);
          let groups: string[] = [];
          if (user && user.signInUserSession?.accessToken?.payload['cognito:groups']) {
            groups = user.signInUserSession.accessToken.payload['cognito:groups'];
          }
          if (loginResponse?.response) {
            const {
              emailAddress,
              aliasInitial,
              aliasInitial2,
              aliasName,
              appUserPk,
              firstName,
              lastName,
              isCasinoAccount,
              showLoadingReels,
              slotCheckSearchEnabled,
            } = loginResponse.response.user;
            AuthHelper.SetToken(loginResponse.response.token as string);
            // Inactivity logout: same rule as players (staff accounts have no verified
            // Player ID link, so 1h unless the data says otherwise).
            startSession(Date.now(), loginResponse.response.user?.linkedAccounts);
            const lastLogin = loginResponse?.response?.lastLogin;
            const last_login_timestamp_counter = lastLogin?.last_login_timestamp_counter;
            const shoUserConfirm: boolean =
              !last_login_timestamp_counter || last_login_timestamp_counter < 1 ? true : false;
            const aliasInformation: Partial<IUserInfo> = {
              aliasInitial: aliasInitial,
              aliasInitial2: aliasInitial2,
              aliasName: aliasName,
            };
            commands.InitUserInfo(username, username, emailAddress as string, appUserPk, groups);
            commands.SetTourState(loginResponse.response.user?.hasCompletedOnboarding as boolean);
            commands.SetUserAlias({ ...aliasInformation });
            const userInformation: Partial<IUserInfo> = {
              firstName: firstName,
              lastName: lastName,
              emailAddress: emailAddress,
              isCasinoAccount,
              // Opt-in Explore slot-reel loading cards (users.showLoadingReels).
              showLoadingReels: showLoadingReels ?? null,
              // Slot Check Search access (users.slotCheckSearchEnabled; Login returns the full user).
              slotCheckSearchEnabled: slotCheckSearchEnabled ?? null,
            };
            commands.SetUserInformation({ ...userInformation });
            commands.SetUserConfirm(shoUserConfirm);
            // Resume last-viewed casino for casino-account login (same users_casinos source).
            markResumeLastViewedCasino();
            router.push('/auth/choosecasinos');
          }
          dismiss()
            .then(() => {
              return;
            })
            .catch(() => {
              return;
            });
        },
        error() {
          void presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message: 'Login has failed, please try again if this persists please contact support.',
          });
          dismiss()
            .then(() => {
              return;
            })
            .catch(() => {
              return;
            });
        },
      });
    } catch (error) {
      let message = 'Unknown Error';
      if (error instanceof Error) message = error.message;
      switch (message) {
        case 'User is not confirmed.':
          message =
            'Sorry, your email is not confirmed. Please check your inbox for a confirmation email.';
          await Auth.resendSignUp(data.email.trim().toLowerCase());
          commands.InitUserInfo(data.email, data.email);
          router.push('/auth/verifyaccount');
          break;
        case 'Incorrect username or password.':
          message = 'Sorry, Incorrect password.';
          break;
        case 'User does not exist.':
          message = 'Sorry, User does not exist.';
          break;
        default:
          if (error instanceof Error) message = error.message;
          break;
      }
      void presentToast({
        position: 'top',
        duration: 5000,
        color: 'danger',
        message: message,
      });
      dismiss()
        .then(() => {
          return;
        })
        .catch(() => {
          return;
        });
    }
  };

  return (
    <div className="login-v2-form">
      <form
        id="confirm-page"
        onSubmit={onPromise(handleSubmit((data: FormData) => void onSubmit(data)))}
      >
        {/* Email field */}
        <div className="login-v2-field-wrap login-v2-pass-wrap">
          <IonIcon
            icon={mailOutline}
            className="login-v2-field-prefix-icon login-v2-field-prefix-icon--mid"
          />
          <div className="login-v2-pass-content">
            <Controller
              name="email"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <IonInput
                  type="email"
                  placeholder="Enter your email"
                  value={value}
                  onIonChange={(e) => onChange(e.detail.value ?? '')}
                  autocomplete="email"
                  id="username-field"
                />
              )}
              rules={{ required: true }}
            />
          </div>
        </div>
        {errors.email?.message && (
          <div className="login-v2-field-error">
            <IonIcon icon={warningOutline} />
            <span>{errors.email.message}</span>
          </div>
        )}

        {/* Password field */}
        <div className="login-v2-field-wrap login-v2-pass-wrap">
          <IonIcon
            icon={lockClosedOutline}
            className="login-v2-field-prefix-icon login-v2-field-prefix-icon--mid"
          />
          <div className="login-v2-pass-content">
            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({ field: { onChange } }) => (
                <IonInput
                  type={showPasswordNew ? 'text' : 'password'}
                  placeholder="Enter your password"
                  onIonChange={onChange}
                  autocomplete="current-password"
                  id="password-field"
                />
              )}
              rules={{ required: true }}
            />
          </div>
          <div className="login-v2-eye-btn" onClick={() => setShowPasswordNew((v) => !v)}>
            <IonIcon icon={showPasswordNew ? eyeOffOutline : eyeOutline} />
          </div>
        </div>
        {errors.password?.message && (
          <div className="login-v2-field-error">
            <IonIcon icon={warningOutline} />
            <span>{errors.password.message}</span>
          </div>
        )}

        <IonButton type="submit" id="login-submit" expand="block" className="login-v2-submit-btn">
          Log In
        </IonButton>

        <div className="login-v2-meta-row">
          <Link to="/auth/enterprise-forgotpassword" className="login-v2-forgot-link">
            Forgot password
          </Link>
        </div>
      </form>

      <div className="login-v2-divider" />

      <p className="login-v2-enterprise">
        Phone number login?{' '}
        <Link to="/auth/login" className="login-v2-enterprise-switch">
          Switch
        </Link>
      </p>
    </div>
  );
};
